import React, { useState, useEffect } from 'react';
import { X, Cookie } from 'lucide-react';
import AccessibleModal from '../shared/AccessibleModal';
import {
  COOKIE_CONSENT_VERSION,
  getCookiePreferences,
  saveCookiePreferences,
} from '../layout/CookieBanner';

interface CookieSettingsModalProps {
  onClose: () => void;
}

export default function CookieSettingsModal({ onClose }: CookieSettingsModalProps) {
  const [analytics, setAnalytics] = useState(false);
  const [hasPrefs, setHasPrefs] = useState(false);

  useEffect(() => {
    const prefs = getCookiePreferences();
    if (prefs) {
      setAnalytics(prefs.analytics);
      setHasPrefs(true);
    }
  }, []);

  const handleSave = () => {
    saveCookiePreferences({
      version: COOKIE_CONSENT_VERSION,
      essential: true,
      analytics,
    });
    onClose();
  };

  const handleDeclineAll = () => {
    saveCookiePreferences({
      version: COOKIE_CONSENT_VERSION,
      essential: true,
      analytics: false,
    });
    onClose();
  };

  return (
    <AccessibleModal onClose={onClose} ariaLabel="Cookie settings" style={{ maxWidth: 480 }}>
      <div className="modal-header">
        <h2><Cookie size={18} aria-hidden="true" /> Cookie Settings</h2>
        <button className="btn btn-ghost btn-small" onClick={onClose} aria-label="Close cookie settings">
          <X size={18} aria-hidden="true" />
        </button>
      </div>
      <div style={{ padding: '0 1.5rem 1.5rem' }}>
        <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
          Manage your cookie preferences. Essential cookies are required and cannot be disabled.
        </p>

        {/* Essential cookies — always on */}
        <div style={{ background: 'var(--bg-secondary, #f5f5f5)', borderRadius: 8, padding: '1rem', marginBottom: '0.75rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <strong>Essential Cookies</strong>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: '0.25rem 0 0' }}>
                Required for login and session management.
              </p>
            </div>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
              Always on
            </span>
          </div>
        </div>

        {/* Analytics cookies — toggleable */}
        <div style={{ background: 'var(--bg-secondary, #f5f5f5)', borderRadius: 8, padding: '1rem', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <strong>Analytics Cookies</strong>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: '0.25rem 0 0' }}>
                Help us understand how visitors use Fox &amp; Quill so we can improve the experience.
              </p>
            </div>
            <label className="toggle-switch" style={{ marginLeft: '1rem', flexShrink: 0 }}>
              <input
                type="checkbox"
                checked={analytics}
                onChange={(e) => setAnalytics(e.target.checked)}
                aria-label="Enable analytics cookies"
              />
              <span className="toggle-slider" />
            </label>
          </div>
        </div>

        {hasPrefs && (
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
            Current preference: Analytics <strong>{analytics ? 'enabled' : 'disabled'}</strong>
          </p>
        )}

        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={handleDeclineAll}>
            Essential Only
          </button>
          <button className="btn btn-primary" onClick={handleSave}>
            Save Preferences
          </button>
        </div>
      </div>
    </AccessibleModal>
  );
}
